/**
 * Turning bare public ids in prose into links to the resource they name.
 *
 * Agents write ids into task descriptions and comments as plain text — "emails
 * emsg_758cf947c2df4051abc4c555, emsg_eaa91c98017941d29222e368" — because that
 * is what the tools handed them. A reader then has to copy the id and go find
 * it. This module is the shared half of the fix: one matcher and one resolver
 * seam, consumed by both markdown surfaces — the TipTap editor's decoration
 * plugin and the read-only renderer's component overrides — so a token
 * linkifies the same way wherever it is rendered.
 *
 * Nothing here rewrites stored text. The editor draws links as view-only
 * decorations, so a description round-trips through markdown byte-identical.
 */

import { parsePublicId, type PublicIdResourceType } from "@quovy/types";

/**
 * Every public id is `<registered prefix>_<24 lowercase hex>` — the schema's
 * `substring(replace(gen_random_uuid()::text, '-', '') from 1 for 24)` default,
 * uniform across all 138 tables that mint one.
 *
 * The 24-hex suffix is what makes this safe to run over free prose. The
 * registry holds short, English-looking prefixes (`key`, `int`, `app`, `sub`,
 * `env`, `loc`, `evt`), so matching on the prefix alone would linkify ordinary
 * snake_case words like `key_value` or `sub_total`. Requiring the exact suffix
 * shape means a false positive has to be 24 hex characters long by accident.
 */
const PUBLIC_ID_TOKEN = /\b([a-z]{2,5})_([0-9a-f]{24})\b/g;

export type PublicIdMatch = {
  /** Index of the first character of the id within the scanned string. */
  start: number;
  /** Index one past the last character. */
  end: number;
  id: string;
  type: PublicIdResourceType;
};

/**
 * Every public id in `text`, in order. Unregistered prefixes are skipped — an
 * id we cannot name is an id we cannot route, and underlining it would promise
 * a destination that does not exist.
 */
export function findPublicIds(text: string): PublicIdMatch[] {
  const matches: PublicIdMatch[] = [];
  // Fresh lastIndex per call: the regex is module-level and /g is stateful.
  PUBLIC_ID_TOKEN.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = PUBLIC_ID_TOKEN.exec(text)) !== null) {
    const parsed = parsePublicId(match[0]);
    if (!parsed) continue;
    matches.push({
      start: match.index,
      end: match.index + match[0].length,
      id: parsed.id,
      type: parsed.type,
    });
  }
  return matches;
}

/**
 * Resolves a public id to an href, or `undefined` when the resource has no
 * page to open. `@quovy/ui` cannot know the app's routes, so the host supplies
 * this; in `apps/web` it is the `resource-routes` registry behind `useWsPath`.
 */
export type PublicIdHrefResolver = (id: string, type: PublicIdResourceType) => string | undefined;
